import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, ShieldCheck, LogOut, ChevronDown, ScanLine } from "lucide-react";
import SpikeLogo from "@/components/site/SpikeLogo";
import BookLink from "@/components/site/BookLink";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";

const baseLinks = [
  { to: "/", label: "Home" },
  { to: "/book", label: "Book Nets" },
  { to: "/checkin", label: "Check In" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { show_membership } = useSettings();
  const links = show_membership ? [...baseLinks, { to: "/membership", label: "Membership" }] : baseLinks;

  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 left-0 right-0 z-50 bg-black/85 backdrop-blur-md border-b border-spike-blue/15"
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="header-logo-link" className="flex items-center">
            <SpikeLogo />
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 font-orbitron text-[0.72rem] font-bold uppercase tracking-[0.18em] transition-colors ${
                    isActive
                      ? "text-spike-blue-light"
                      : "text-spike-text/80 hover:text-spike-blue-light"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+19056789725"
              data-testid="header-phone"
              className="flex items-center gap-2 text-spike-text-muted hover:text-spike-blue-light transition-colors text-sm font-exo"
            >
              <Phone className="w-4 h-4 text-spike-blue-light" strokeWidth={2} />
              (905) 678-9725
            </a>

            {user ? (
              <div className="relative">
                <button
                  type="button"
                  data-testid="header-user-menu-toggle"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-spike-elevated transition-colors"
                >
                  <div className="w-8 h-8 bg-spike-blue rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="text-sm font-medium text-spike-text">
                    {user.name?.split(" ")[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-spike-dim" />
                </button>
                {menuOpen && (
                  <div
                    data-testid="header-user-menu"
                    className="absolute right-0 mt-2 w-52 bg-spike-elevated border border-white/10 rounded-lg shadow-card-hover overflow-hidden"
                  >
                    <Link
                      to="/account"
                      data-testid="user-menu-account"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-spike-text border-b border-white/10"
                    >
                      <User className="w-4 h-4 text-spike-blue-light" /> My account
                    </Link>
                    {user.is_staff && (
                      <Link
                        to="/scantocheckin"
                        data-testid="user-menu-scanner"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-spike-text border-b border-white/10"
                      >
                        <ScanLine className="w-4 h-4 text-spike-blue-light" /> Check-in scanner
                      </Link>
                    )}
                    {user.is_admin && (
                      <Link
                        to="/admin"
                        data-testid="user-menu-admin"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-spike-text border-b border-white/10"
                      >
                        <ShieldCheck className="w-4 h-4 text-spike-blue-light" /> Admin dashboard
                      </Link>
                    )}
                    <button
                      type="button"
                      data-testid="user-menu-logout"
                      onClick={handleLogout}
                      className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-spike-text"
                    >
                      <LogOut className="w-4 h-4 text-spike-blue-light" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-link"
                className="font-orbitron text-[0.72rem] font-bold uppercase tracking-[0.18em] text-spike-text/80 hover:text-spike-blue-light transition-colors px-3 py-2"
              >
                Sign in
              </Link>
            )}

            <BookLink
              data-testid="header-cta-book"
              className="bg-spike-blue hover:bg-spike-blue-hover text-black font-orbitron font-bold uppercase tracking-[0.18em] text-[0.72rem] px-5 py-2.5 rounded-sm transition-colors shadow-md shadow-spike-blue/40 hover:shadow-spike-blue/60"
            >
              Book Lane
            </BookLink>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="md:hidden text-spike-text p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="md:hidden pb-4 border-t border-white/10 mt-1 pt-3 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-md font-orbitron text-xs font-bold uppercase tracking-[0.2em] ${
                    isActive ? "bg-spike-blue/10 text-spike-blue-light" : "text-spike-text/85"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/account" className="block px-3 py-2.5 rounded-md text-sm font-medium text-spike-text">My account</Link>
                {user.is_admin && (
                  <Link to="/admin" className="block px-3 py-2.5 rounded-md text-sm font-medium text-spike-text">Admin dashboard</Link>
                )}
                <button type="button" onClick={handleLogout} className="w-full text-left block px-3 py-2.5 rounded-md text-sm font-medium text-spike-text">Logout</button>
              </>
            ) : (
              <Link to="/login" data-testid="mobile-login-link" className="block px-3 py-2.5 rounded-md font-orbitron text-xs font-bold uppercase tracking-[0.2em] text-spike-text/85">Sign in</Link>
            )}
            <Link
              to="/book"
              data-testid="mobile-cta-book"
              className="block w-full text-center bg-spike-blue text-black font-orbitron font-bold uppercase tracking-[0.18em] text-xs px-5 py-3 rounded-sm mt-2"
            >
              Book Lane
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
